// Research Engine — UI language switcher (ko / en / ja / zh / es).
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "../../i18n";
import { Icon } from "./icon";
import { cn } from "./primitives";

export function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const active = i18n.language.split("-")[0];
  const current = SUPPORTED_LANGUAGES.find((l) => l.code === active) ?? SUPPORTED_LANGUAGES[0];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t("common.language")}
        className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[13px] text-ink-dim hover:text-ink hover:bg-surface-2 transition-colors"
      >
        <Icon name="globe" size={14} />
        <span className="hidden sm:inline">{current.label}</span>
        <Icon name="chevron-down" size={13} className="text-ink-faint" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-40 bg-surface border border-border rounded-lg overflow-hidden shadow-[0_8px_30px_-12px_rgba(0,0,0,0.15)] z-50 p-1">
          {SUPPORTED_LANGUAGES.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => {
                void i18n.changeLanguage(l.code);
                setOpen(false);
              }}
              className={cn(
                "w-full flex items-center justify-between h-9 px-2.5 rounded-md text-[13.5px] transition-colors text-left",
                l.code === active ? "bg-surface-2 text-ink" : "text-ink-dim hover:text-ink hover:bg-surface-2",
              )}
            >
              {l.label}
              {l.code === active && <Icon name="check" size={14} className="text-accent-ink" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
